import React, { useState } from 'react';
import { api } from '../services/api';
import { LockKeyhole, Loader2 } from 'lucide-react';

interface AuthProps {
  onSuccess: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onSuccess }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin) return;
    
    setLoading(true);
    setError('');
    
    try {
      const isValid = await api.verifyPin(pin);
      if (isValid) {
        onSuccess();
      } else {
        setError('Incorrect PIN. Access Denied.');
        setPin('');
      }
    } catch (err) {
        console.error(err);
        setError('Connection error. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-primary flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-100">
        <div className="flex justify-center mb-5">
          <div className="p-4 bg-red-50 rounded-full ring-4 ring-red-50/50">
            <LockKeyhole size={32} className="text-primary" />
          </div>
        </div>
        
        <div className="text-center mb-8">
            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Welcome back to</p>
            <h1 className="text-4xl font-black tracking-tighter whitespace-nowrap">
              <span className="text-secondary">Idea</span>
              <span className="text-primary ml-1">Notes</span>
            </h1>
            <p className="text-gray-500 text-sm mt-2 font-medium">Enter PIN to manage your content</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <input
              type="password"
              value={pin}
              onChange={(e) => {
                  setPin(e.target.value);
                  setError('');
              }}
              placeholder="Enter PIN"
              className="w-full text-center text-2xl tracking-widest px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-300 placeholder:tracking-normal text-gray-800 font-bold bg-white"
              maxLength={6}
              autoFocus
              disabled={loading}
            />
          </div>
          
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                <p className="text-red-500 text-sm text-center font-medium">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !pin}
            className="w-full bg-primary text-white py-3.5 rounded-xl font-bold text-lg hover:bg-red-600 active:scale-[0.98] transition-all shadow-lg shadow-red-200 disabled:opacity-70 disabled:active:scale-100 flex items-center justify-center"
          >
            {loading ? (
                <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Verifying...
                </>
            ) : 'Unlock App'}
          </button>
        </form>
      </div>
    </div>
  );
};